import Keycloak from 'keycloak-js';

let _keycloak = null;

/**
 * Keycloak SSO is opt-in: only active when REACT_APP_KC_ENABLED=true.
 * While disabled the app relies solely on local login (/auth/signin) and
 * no request is ever made to the Keycloak server.
 */
export function isKeycloakEnabled() {
  return process.env.REACT_APP_KC_ENABLED === 'true';
}

/**
 * Initialize Keycloak for this app.
 * Call once in index.js before rendering the React tree.
 *
 * @param {string} clientId - The Keycloak client ID for this specific app
 * @returns {Promise<Keycloak|null>} null when Keycloak is disabled
 */
export function initAuth(clientId) {
  if (!isKeycloakEnabled()) return Promise.resolve(null);
  if (_keycloak) return Promise.resolve(_keycloak);

  _keycloak = new Keycloak({
    url:      process.env.REACT_APP_KC_URL      || 'https://auth-staging.cbqaglobal.com',
    realm:    process.env.REACT_APP_KC_REALM    || 'cbqaglobal',
    clientId: process.env.REACT_APP_KC_CLIENT_ID || clientId,
  });

  return _keycloak
    .init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      // When the browser blocks 3rd-party cookies (auth domain differs from app
      // domain), keycloak-js would otherwise fall back to a full-page redirect
      // with prompt=none on every load. Keep check-sso strictly silent.
      silentCheckSsoFallback: false,
      pkceMethod: 'S256',
      checkLoginIframe: false,
    })
    .then(() => _keycloak);
}

/** Get the initialized Keycloak instance (after initAuth resolves). */
export function getKeycloak() {
  if (!_keycloak) throw new Error('@cbqaglobal/auth: call initAuth() before getKeycloak()');
  return _keycloak;
}

/** Login as staff — redirects to Microsoft M365 SSO. */
export function loginStaff()    { getKeycloak().login({ idpHint: 'microsoft' }); }

/** Login as customer — shows Keycloak login form (OneDataHub). */
export function loginCustomer() { getKeycloak().login(); }

/** Logout from Keycloak (clears session on broker too). */
export function logout()        { getKeycloak().logout(); }

/** Returns true if the user is currently authenticated. */
export function isAuthenticated() { return _keycloak?.authenticated === true; }

/** Returns the raw JWT access token string. */
export function getToken()      { return getKeycloak().token; }

/**
 * Refreshes the token if it expires within `minValidity` seconds (default 30).
 * Returns the (possibly refreshed) access token.
 */
export async function getValidToken(minValidity = 30) {
  await getKeycloak().updateToken(minValidity);
  return getKeycloak().token;
}

/** Returns the parsed token payload. */
export function getTokenParsed() { return getKeycloak().tokenParsed; }

/** Returns the user's email. */
export function getUserEmail()   { return getKeycloak().tokenParsed?.email; }

/** Returns the user's display name. */
export function getUserName()    {
  const t = getKeycloak().tokenParsed;
  return t ? (t.name || t.preferred_username) : null;
}

/** Returns realm roles array (e.g. ['ROLE_ADMIN', 'ROLE_AUDITOR']). */
export function getRoles()       { return getKeycloak().tokenParsed?.realm_access?.roles || []; }

/** Returns true if user has the given realm role. */
export function hasRole(role)    { return getRoles().includes(role); }

/**
 * Axios request interceptor — auto-attaches Bearer token.
 * Usage: axios.interceptors.request.use(authInterceptor)
 */
export async function authInterceptor(config) {
  if (!_keycloak) return config;
  if (config.headers?.Authorization) return config; // local session token wins
  try {
    const token = await getValidToken(30);
    if (token) config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  } catch (_) { /* not authenticated — let request go without token */ }
  return config;
}
